/**
 * The blackjack table as the scene draws it — plain data, one hand at a time.
 *
 * ============================== WHY A VIEW OF ITS OWN ============================
 * Poker renders from the engine's `TableState` and the Director presents it at pace
 * (`display.ts`). The blackjack engine has no `TableState`: `BlackjackTable` is a
 * mutable object the room reads directly, and it deals a whole round — every card to
 * every seat and both of the dealer's — inside one `startHand()`, and plays the dealer
 * out and settles inside the `act()` that finishes the last hand. The engine is right
 * to do that and is not touched; so the SCENE reads this instead: the same table, as it
 * has been SHOWN so far. `viewOf()` is the engine's truth flattened; the driver builds
 * the intermediate views a card at a time and the tests hold that the last one it
 * presents equals `viewOf()` of the settled table.
 *
 * The dealer's hole card is `null` until it is turned. The engine keeps it; the view
 * never carries its value face down, so nothing that renders from a view can leak it.
 * ===============================================================================
 */
import type { BlackjackRules } from "@engine/blackjack/rules";
import { handTotal, type Action, type BlackjackTable, type Phase, type PlayerHand } from "@engine/blackjack/table";

export type Outcome = NonNullable<PlayerHand["outcome"]>;

export interface BjHandView {
  cards: number[];
  /** In bet units, as the engine keeps it. Doubled hands carry the doubled bet. */
  bet: number;
  doubled: boolean;
  fromSplit: boolean;
  outcome: Outcome | null;
  /** Net of the stake, once settled. */
  settled: number | null;
}

export interface BjSeatView {
  index: number;
  isHero: boolean;
  hands: BjHandView[];
  /** In bet units: half the opening bet when taken. */
  insurance: number;
  insuranceSettled: number | null;
}

export interface BjView {
  handNumber: number;
  phase: Phase;
  /** The dealer's cards as shown: `null` is a card lying face down. */
  dealer: (number | null)[];
  turn: { seat: number; hand: number } | null;
  seats: BjSeatView[];
}

const hand = (h: PlayerHand): BjHandView => ({
  cards: [...h.cards], bet: h.bet, doubled: h.doubled, fromSplit: h.fromSplit,
  outcome: h.outcome, settled: h.settled,
});

/** The engine's table, flattened — everything the scene may show once the hand is presented. */
export function viewOf(t: BlackjackTable): BjView {
  const open = t.phase === "dealer" || t.phase === "settled";
  return {
    handNumber: t.handNumber,
    phase: t.phase,
    dealer: t.dealer.map((c, i) => (i === 1 && !open ? null : c)),
    turn: t.turn ? { ...t.turn } : null,
    seats: t.seats.map((s, index) => ({
      index, isHero: s.isHero, hands: s.hands.map(hand), insurance: s.insurance,
      insuranceSettled: t.phase === "settled" && s.insurance > 0 ? s.insuranceSettled : null,
    })),
  };
}

/** An empty table: seats with nothing in front of them. What is drawn before the first deal. */
export function emptyView(seats: number, handNumber = 0): BjView {
  return {
    handNumber, phase: "dealing", dealer: [], turn: null,
    seats: Array.from({ length: seats }, (_, index) => ({
      index, isHero: index === 0, hands: [], insurance: 0, insuranceSettled: null,
    })),
  };
}

export const cloneView = (v: BjView): BjView => JSON.parse(JSON.stringify(v)) as BjView;

/* --------------------------------------------------------------- the words --- */

/**
 * A hand's total, written the way a table says it. `null` cards (a face-down hole)
 * are not counted — the dealer "shows" what is face up.
 */
export function totalLabel(cards: readonly (number | null)[]): string {
  const up = cards.filter((c): c is number => c !== null);
  if (!up.length) return "";
  const { total, soft } = handTotal(up);
  if (total > 21) return `${total} — over`;
  return soft && total !== 21 ? `${total} soft` : String(total);
}

/** The short form, for a plate: "14", "S17", "BJ", "22". */
export function totalShort(cards: readonly (number | null)[], natural = false): string {
  const up = cards.filter((c): c is number => c !== null);
  if (!up.length) return "";
  if (natural) return "BJ";
  const { total, soft } = handTotal(up);
  return soft && total < 21 ? `S${total}` : String(total);
}

export const isBust = (cards: readonly number[]): boolean => cards.length > 0 && handTotal(cards).total > 21;

/** A natural as the engine settles one: two cards, 21, not from a split. */
export const naturalView = (h: BjHandView): boolean =>
  !h.fromSplit && h.cards.length === 2 && handTotal(h.cards).total === 21;

export type HeroMove = Action | "insurance" | "decline";

/**
 * What a settled hand is called on screen — moved here from `routes/Blackjack.tsx`
 * (where it was written and argued for) because the felt that draws it moved.
 *
 * The engine's outcome union is `win | lose | push | blackjack`, and rendered straight
 * through the felt said "· win" beside a player's cards — the word the room keeps off
 * the page (`tests/blackjack-copy.test.ts` reads this file too). The engine keeps its
 * vocabulary; the screen gets this one.
 */
export const OUTCOME_WORD: Record<Outcome, string> = {
  blackjack: "natural",
  win: "held up",
  lose: "beaten",
  push: "push",
};

/* ---------------------------------------------------------- the cloth --- */

/** 1.5 -> "3 TO 2", 1.2 -> "6 TO 5", 2 -> "2 TO 1". */
export function paysWords(ratio: number): string {
  for (let d = 1; d <= 10; d++) {
    const n = ratio * d;
    if (Math.abs(n - Math.round(n)) < 1e-9) return `${Math.round(n)} TO ${d}`;
  }
  return `${ratio} TO 1`;
}

/** Every word the cloth prints, from the rules — exported so the tests read what is drawn. */
export function printWords(rules: BlackjackRules): { pays: string; insurance: string | null; dealer: string } {
  return {
    pays: `BLACKJACK PAYS ${paysWords(rules.blackjackPays)}`,
    insurance: rules.offerInsurance ? `INSURANCE PAYS ${paysWords(rules.insurancePays)}` : null,
    dealer: rules.dealerStandsSoft17
      ? "DEALER MUST DRAW TO 16 AND STAND ON ALL 17S"
      : "DEALER MUST DRAW TO 16 AND HIT SOFT 17",
  };
}
